/**
 * Tabs.tsx — a small shared tablist / tabpanel primitive (v0.6.6).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the callback ticket drawer re-lays as a TABBED surface (Details /
 * Activity / Patient) and the admin console + clinician workspace hand-roll
 * their own `role="tablist"` markup today. Rather than grow a fourth copy, this
 * is the one WAI-ARIA tabs pattern: a `role="tablist"` of `role="tab"` buttons
 * with roving `tabIndex` (only the active tab is Tab-reachable) + arrow / Home /
 * End key navigation (automatic activation — focus moves and selects together),
 * and a matching `TabPanel` that renders only its active panel and wires the
 * `aria-controls` / `aria-labelledby` id pair. Controlled: the owner holds the
 * active-tab state (a drawer keeps its default tab per open), so the primitive
 * stays state-free and reusable.
 *
 * 2026-09-06 — TAB MARKERS ARE NOW THREE SHAPES, not one. A tab can say what is
 * waiting inside it before anyone opens it: an amber triangle for clinical
 * attention, a dot for something outstanding, a number pill for N things to
 * read. See {@link TabMarker} for why the severe one had to be separated from
 * the other two.
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { useRef } from 'react';
import { WarningIcon } from './kit';

/**
 * THE THREE TAB MARKERS (2026-09-06) — widened from the single amber-triangle
 * `warning?: string` this file carried since 2026-08-07.
 *
 * ⚠️ WHY THREE AND NOT ONE. The old marker was the ONLY way to say "there is
 * something in this tab", so everything with something to say wanted it: the
 * double-prescribing key alert, an outstanding admin ask, a possible double
 * charge, a list of merged journeys. Give them all the same amber triangle and
 * the triangle stops meaning "a clinician must look at this before
 * prescribing" and starts meaning "this tab has content" — which is the exact
 * failure the double-prescribing marker exists to prevent. Separating the
 * shapes lets the severe one stay rare.
 *
 * `name` KEEPS THE ORIGINAL CONTRACT: it is the marker's ACCESSIBLE NAME and it
 * is appended to the tab's own name, so a screen reader reads "Review, Key
 * alert — existing medication" without the tab ever being opened. Pass wording
 * somebody can act on, never "warning" or a bare number.
 *
 * WCAG 2.1 AA 1.4.1 — NEVER COLOUR OR SHAPE ALONE. A tab marker is the
 * house rule's sanctioned icon-only case: a secondary qualifier sitting beside
 * a visible status (the tab's own label), with the full wording on
 * `role="img"` + `aria-label` + `title`. The `title` is deliberate and is more
 * than the old sr-only span gave — a sighted mouse user can now hover a dot and
 * find out what it means without opening the tab.
 */
export type TabMarker =
  /**
   * CLINICAL ATTENTION — the amber triangle. Key alerts, clinical flags,
   * answers changed after an earlier check.
   *
   * ⚠️ ONE SEVERITY ONLY. The moment a second class of thing wears this, a
   * clinician stops reading it. If a signal is merely outstanding, it is
   * `waiting`; if it is a list to get through, it is `count`.
   */
  | { kind: 'alert'; name: string }
  /**
   * SOMETHING OUTSTANDING — a small dot. An admin ask with nobody on it, a
   * duplicate's possible double charge. Quieter than `alert` on purpose: it
   * says "unfinished", not "check this before you prescribe".
   */
  | { kind: 'waiting'; name: string }
  /**
   * N THINGS TO READ — a number pill. Bounded reading, not a warning.
   *
   * ⚠️ NEVER PASS 0. A "0" pill asserts an empty list where the honest
   * rendering is no marker at all; the caller drops the marker instead. This
   * primitive renders faithfully what it is handed and does not second-guess a
   * count, because a zero that reaches here is a caller bug worth seeing.
   */
  | { kind: 'count'; name: string; count: number };

export interface TabDef {
  /** Stable id — the URL/aria key, NOT the visible label. */
  id: string;
  label: string;
  /**
   * Push this tab (and every tab after it) to the RIGHT edge of the tab row
   * (UX Wave 9 §1 — the deal drawer's Audit tab floats right as quiet
   * governance chrome). Purely visual: keyboard order and the panel wiring
   * follow the array order unchanged.
   */
  floatRight?: boolean;
  /**
   * The marker worn beside this tab's label — what is waiting inside, said
   * before anyone opens it. See {@link TabMarker} for the three kinds and the
   * rule about which one a signal earns.
   */
  marker?: TabMarker;
}

const tablistStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--gps-s-2)',
  flexWrap: 'wrap',
  borderBottom: '1px solid var(--gps-border)',
  marginBottom: 'var(--gps-s-4)',
};

function tabStyle(active: boolean): CSSProperties {
  return {
    minHeight: '44px',
    padding: '10px 16px',
    background: 'none',
    border: 0,
    borderBottom: `3px solid ${active ? 'var(--gps-primary)' : 'transparent'}`,
    color: active ? 'var(--gps-green-text)' : 'var(--gps-muted)',
    font: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  };
}

/**
 * One tab marker. The shape is the signal; the words are the meaning.
 *
 * `role="img"` + `aria-label` is what makes this work for the tab's accessible
 * NAME: name-from-content walks into the button's children, and an `img` with a
 * label contributes that label and nothing else — so the tab reads "Review Key
 * alert — existing medication" and never leaks the glyph or a bare digit into
 * the name. `title` gives the sighted mouse user the same sentence on hover,
 * which the sr-only span this replaced could not.
 */
function Marker({ marker }: { marker: TabMarker }) {
  // Identical on all three: the wording is the marker, whatever it is drawn as.
  const named = { role: 'img' as const, 'aria-label': marker.name, title: marker.name };

  if (marker.kind === 'alert') {
    return (
      <span {...named} style={{ color: 'var(--gps-warn-text)', display: 'inline-flex', alignItems: 'center' }}>
        <WarningIcon size={15} />
      </span>
    );
  }

  if (marker.kind === 'waiting') {
    return (
      <span
        {...named}
        style={{
          // A DOT, NOT A SMALL TRIANGLE. Under a quick scan a shrunken warning
          // glyph reads as the alert marker, which would re-merge the two
          // severities this split exists to keep apart. A filled circle shares
          // no silhouette with the triangle at any size.
          //
          // 8px of --gps-warn-text on the drawer surface clears WCAG 1.4.11's
          // 3:1 non-text bar comfortably (#8a5a00 on #ffffff is 6.4:1), and the
          // colour is redundant with the shape and the label either way.
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'var(--gps-warn-text)',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <span
      {...named}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '18px',
        height: '18px',
        padding: '0 5px',
        boxSizing: 'border-box',
        borderRadius: '9px',
        background: 'var(--gps-surface-2)',
        color: 'var(--gps-muted)',
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {marker.count}
    </span>
  );
}

/**
 * The tablist. `idBase` scopes the tab/panel id pair so several tab groups can
 * coexist on one page without colliding — pass the SAME `idBase` to every
 * matching {@link TabPanel}.
 */
export function Tabs({
  label,
  tabs,
  activeId,
  onChange,
  idBase,
}: {
  label: string;
  tabs: TabDef[];
  activeId: string;
  onChange: (id: string) => void;
  idBase: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = tabs.findIndex((t) => t.id === activeId);
    if (index < 0) return;
    let next = index;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const id = tabs[next].id;
    onChange(id);
    // Move focus with selection (automatic activation) so keyboard users land
    // on the newly-selected tab.
    refs.current[id]?.focus();
  }

  // The FIRST floatRight tab carries the auto left margin — it (and any tab
  // after it) sits against the right edge of the row.
  const firstFloatRight = tabs.findIndex((t) => t.floatRight);

  return (
    <div role="tablist" aria-label={label} onKeyDown={onKeyDown} style={tablistStyle}>
      {tabs.map((tab, index) => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[tab.id] = el;
            }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${tab.id}`}
            aria-selected={active}
            aria-controls={`${idBase}-panel-${tab.id}`}
            // Roving tabindex — only the active tab is in the Tab order.
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.id)}
            style={{
              ...tabStyle(active),
              ...(index === firstFloatRight ? { marginLeft: 'auto' } : {}),
              ...(tab.marker ? { display: 'inline-flex', alignItems: 'center', gap: '6px' } : {}),
            }}
          >
            {tab.label}
            {tab.marker ? <Marker marker={tab.marker} /> : null}
          </button>
        );
      })}
    </div>
  );
}

/** The panel for one tab. Renders nothing unless it is the active tab. */
export function TabPanel({
  idBase,
  tabId,
  activeId,
  children,
}: {
  idBase: string;
  tabId: string;
  activeId: string;
  children: ReactNode;
}) {
  if (tabId !== activeId) return null;
  return (
    <div role="tabpanel" id={`${idBase}-panel-${tabId}`} aria-labelledby={`${idBase}-tab-${tabId}`} tabIndex={0}>
      {children}
    </div>
  );
}
